import queryString from 'query-string';

import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import type { GetEntriesFilter } from '~/domains/entries/types/get-entries-filter.type';
import { createQueryString } from '~/domains/common/utils/queryString';

import type { CurrencyType } from './enums/currency-type.enum';
import type { APIUserEntry, UserEntry } from './interfaces/user-entry.interface';
import type { ContestEntry } from './interfaces/entry.interface';
import type { GetMyEntriesPayload } from './interfaces/get-my-entries-payload.interface';
import { contestsServiceInstance } from './contest.service.instance';
import type { EntryWithPicks } from './interfaces/entry-with-picks.interface';
import type { PickForEntry } from './interfaces/picks-for-entry.interface';
import type { PlayerPoolMember } from './interfaces/player-pool-member.interface';
import parseEntriesForContest from './parsers/parseEntriesForContest';
import type { BulkPicksResponse } from './interfaces/bulk-picks';
import { propsServiceInstance } from './props.service.instance';
import type { ContestPropPicksResponse } from './interfaces/contest-prop-picks';
import type { SurvivorProp } from './interfaces/survivor-prop-picks';

const MAX_ENTRIES = 150;

const getEntries = async (payload: GetMyEntriesPayload) => {
  const response = await contestsServiceInstance.post<PaginatedData<ContestEntry>>(
    `/contests/entries`,
    payload
  );
  return response.data;
};

const getUserEntriesForContest = async (
  contestId: string,
  userId: string,
  {
    limit = MAX_ENTRIES,
    offset = 0,
    showEntryNames,
    entryId,
  }: {
    limit?: number;
    offset?: number;
    showEntryNames?: boolean;
    entryId?: string;
  } = {}
): Promise<PaginatedData<UserEntry>> => {
  try {
    const query = queryString.stringify({ limit, offset, showEntryNames, entryId });
    const response = await contestsServiceInstance.get<PaginatedData<APIUserEntry>>(
      `contests/${contestId}/users/${userId}/entries?${query}`
    );

    return parseEntriesForContest(response.data);
  } catch (error) {
    if (error.response?.status === 404) {
      return {
        data: [],
        limit: 0,
        offset: 0,
        total: 0,
      };
    }
    throw error;
  }
};

const getEntryPicks = async (
  contestId: string,
  entryId: string,
  offset = 0,
  limit = 1
): Promise<APIUserEntry> => {
  const response = await contestsServiceInstance.get<APIUserEntry>(
    `/contests/${contestId}/entries${createQueryString({ entryId, offset, limit })}`
  );
  return response.data;
};

const getPicksForEntries = async (
  contestId: string,
  slateId: string
): Promise<EntryWithPicks[]> => {
  try {
    const response = await contestsServiceInstance.get<EntryWithPicks[]>(
      `/contests/${contestId}/slates/${slateId}/entries`
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

const getPicksForEntry = async (
  contestId: string,
  slateId: string,
  entryId: string
): Promise<{ dropWorstCount?: number; picks: PickForEntry[] }> => {
  try {
    const response = await contestsServiceInstance.get<{
      dropWorstCount?: number;
      picks: PickForEntry[];
    }>(`/contests/${contestId}/slates/${slateId}/entries/${entryId}/picks`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { picks: [] };
    }
    throw error;
  }
};

const getBulkPicksForEntries = async (payload: {
  entryIds: string[];
  slateIds: string[];
}): Promise<BulkPicksResponse> => {
  try {
    const response = await contestsServiceInstance.post<BulkPicksResponse>('/picks', payload);

    return response.data;
  } catch (error) {
    if ([404, 400].includes(error.response?.status)) {
      return { entries: {} };
    }
    throw error;
  }
};

const getPropPicksForEntries = async (payload: {
  entryIds: string[];
}): Promise<ContestPropPicksResponse> => {
  try {
    const response = await contestsServiceInstance.get<ContestPropPicksResponse>(
      `/prop-picks?entryIds=${payload.entryIds.join(',')}`
    );

    return response.data;
  } catch (error) {
    if ([404, 400].includes(error.response?.status)) {
      return { entries: [] };
    }
    throw error;
  }
};

const getBulkContestEntries = async (
  payload: GetEntriesFilter
): Promise<PaginatedData<ContestEntry>> => {
  const response = await contestsServiceInstance.post<PaginatedData<ContestEntry>>(
    `/contests/entries`,
    { limit: 100, offset: 0, ...payload }
  );

  return response.data;
};

const updateEntry = async (entryId: string, payload: { name: string }) => {
  const response = await contestsServiceInstance.put(`/entries/${entryId}`, payload);

  return response.data;
};

interface CreateEntryPayload {
  contestId: string;
  contestPassword?: string;
  entriesCount?: number;
  currencyType?: CurrencyType;
  zipCode?: string;
  voucherId?: string;
}

const createEntry = async (
  payload: CreateEntryPayload,
  { skipLocationValidation }: { skipLocationValidation?: boolean } = {}
) => {
  const response = await contestsServiceInstance.post<string[]>(`/v2/entries`, payload, {
    skipLocationValidation,
  });
  return response.data;
};

const cancelEntry = async (entryId: string) => {
  const response = await contestsServiceInstance.delete(`/entries/${entryId}`);
  return response.data;
};

const getPlayerPoolsByTierId = async ({
  contestId,
  slateId,
  tierId,
  entryId,
  offset = 0,
  limit = 20,
  searchQuery,
}: {
  contestId: string;
  slateId: string;
  tierId: number;
  entryId?: string;
  offset?: number;
  limit?: number;
  searchQuery?: string;
}): Promise<
  PaginatedData<PlayerPoolMember> & {
    canMakePicks: boolean;
  }
> => {
  const response = await contestsServiceInstance.get<
    PaginatedData<PlayerPoolMember> & {
      canMakePicks: boolean;
    }
  >(
    `contests/${contestId}/slates/${slateId}/player-pool${createQueryString({
      tierId,
      offset,
      limit,
      search: searchQuery,
      entryId,
    })}`
  );
  return response.data;
};

const getPropPicksForEntry = async (slateId: string, entryId: string) => {
  const response = await propsServiceInstance.get<{
    picks: SurvivorProp[];
    lives_remaining?: number;
    eliminated_slate_id?: string;
    can_make_picks?: boolean;
  }>(`/survivor-props/slate/${slateId}/picks${createQueryString({ entry: entryId })}`);
  return response.data;
};

const getPlayerSurvivorPool = async ({
  slateId,
  entryId,
  contestId,
  offset = 0,
  limit = 20,
  searchQuery,
  gameIds = [],
}: {
  slateId: string;
  entryId: string;
  contestId: string;
  offset?: number;
  limit?: number;
  searchQuery?: string;
  gameIds;
}): Promise<PaginatedData<any>> => {
  const response = await propsServiceInstance.get<PaginatedData<any>>(
    `/survivor-props/slate/${slateId}${createQueryString({
      entry: entryId,
      contestId,
      offset,
      limit,
      search: searchQuery,
      game: gameIds,
    })}`
  );
  return response.data;
};

export interface PickHistoryItem {
  isAutoPick?: boolean;
  player?: {
    id: string;
    name: string;
    tierId?: number;
  };
  slate: {
    id: string;
    name: string;
  };
  playerActivity?: {
    position?: number | string;
    tied?: boolean;
    score?: number;
    money?: number;
    imageUrl?: string;
    headshotUrl?: string;
  };
}

export interface PickHistoryResponse {
  data: PickHistoryItem[];
  limit: number;
  offset: number;
  total: number;
}

const getPickHistory = async ({
  entryId,
  segmentId,
  limit = 100,
  offset = 0,
}: {
  entryId: string;
  segmentId?: string;
  limit?: number;
  offset?: number;
}): Promise<PickHistoryResponse> => {
  const response = await contestsServiceInstance.get<PickHistoryResponse>(
    `/pick-history${createQueryString({ entryId, segmentId, limit, offset })}`
  );
  return response.data;
};

export {
  cancelEntry,
  createEntry,
  getBulkContestEntries,
  getBulkPicksForEntries,
  getEntries,
  getPickHistory,
  getPicksForEntries,
  getPicksForEntry,
  getPlayerPoolsByTierId,
  getUserEntriesForContest,
  updateEntry,
  getPropPicksForEntry,
  getPlayerSurvivorPool,
  getPropPicksForEntries,
  getEntryPicks,
};
